import { IconArrowRight, IconBooks, IconCalendarEvent, IconChevronsLeft, IconChevronsRight, IconClockHour4, IconMapPin, IconPhone, IconUser } from '@tabler/icons-react';
import { useTranslation } from '../../../../i18n/hooks/useTranslation';
import { PosterLogo, PosterSheet, fs, hexToRgba, numberScale, posterFields, valueFitScale } from '../posterParts';

/**
 * "Burst" — a loud, high-energy poster: a colour-blocked hero with sunburst rays, a tilted alert strip, and the vacancy count as a big
 * badge breaking out of the hero into the white lower half (dates as chips, two icon cards, a pill call-to-action, a dark footer).
 * Palette: hero, heroDeep, heroText, accent, ink, number, cta, footer, footerText, footerName.
 */
const FONT = "'Anek Bangla', 'Baloo Da 2', 'Noto Sans Bengali', sans-serif";

function InfoCard({ p, Icon, label, children }) {
    return (
        <div
            className="flex items-center"
            style={{ background: '#fff', borderRadius: '2cqw', borderLeft: `${fs(1.3)} solid ${p.hero}`, padding: `${fs(1.8)} ${fs(2)}`, gap: fs(2), boxShadow: '0 0.6cqw 1.6cqw rgba(15,23,42,0.14)' }}
        >
            <div className="flex flex-none items-center justify-center rounded-full" style={{ width: fs(8), height: fs(8), background: p.hero }}>
                <Icon style={{ width: fs(4.6), height: fs(4.6), color: p.heroText }} stroke={2} />
            </div>
            <div className="min-w-0 flex-1">
                <div style={{ color: p.muted, fontSize: fs(2.5), fontWeight: 700, lineHeight: 1.2 }}>{label}</div>
                {children}
            </div>
        </div>
    );
}

export default function BurstDesign({ job, business, theme, showFooter = true, className = '' }) {
    const { t } = useTranslation();
    const p = theme.palette;
    const f = posterFields(job, business, t);
    const eduScale = valueFitScale(f.edu, 28);
    const ageScale = valueFitScale(f.age, 22);

    const body = (
        <div className="flex flex-1 flex-col">
            {/* ── hero: colour block with rays ── */}
            <div
                className="relative"
                style={{
                    padding: `${fs(5.5)} 5cqw ${fs(2)}`,
                    background: `repeating-conic-gradient(from 0deg at 50% 86%, ${p.ray} 0deg 6deg, rgba(255,255,255,0) 6deg 12deg), radial-gradient(90% 70% at 50% 100%, ${hexToRgba('#ffffff', 0.18)}, transparent 70%), linear-gradient(180deg, ${p.hero} 0%, ${p.heroDeep} 100%)`,
                }}
            >
                <div className="flex items-center" style={{ gap: fs(3) }}>
                    <PosterLogo job={job} size={14} style={{ background: '#fff', borderRadius: '50%', border: `${fs(0.7)} solid ${p.accent}`, padding: fs(1), boxShadow: '0 0.6cqw 1.4cqw rgba(0,0,0,0.25)' }} />
                    <div className="min-w-0 flex-1">
                        <div style={{ color: p.heroText, fontSize: fs(6.2), fontWeight: 800, lineHeight: 1.12 }}>{f.org}</div>
                        {f.position && <div style={{ color: p.heroText, opacity: 0.88, fontSize: fs(3.4), fontWeight: 600, lineHeight: 1.25, marginTop: fs(0.8) }}>{f.position}</div>}
                    </div>
                </div>

                {/* tilted alert strip, wider than the sheet */}
                <div className="flex items-center justify-center" style={{ margin: `${fs(3.6)} -8cqw 0`, transform: 'rotate(-3deg)', background: p.ink, padding: `${fs(1.5)} 0`, gap: fs(2), boxShadow: '0 0.8cqw 1.6cqw rgba(0,0,0,0.3)' }}>
                    <IconChevronsRight style={{ width: fs(5), height: fs(5), color: p.accent }} stroke={3} />
                    <div style={{ color: p.accent, fontSize: fs(5), fontWeight: 900, letterSpacing: '0.02em' }}>{f.label.notice}</div>
                    <IconChevronsLeft style={{ width: fs(5), height: fs(5), color: p.accent }} stroke={3} />
                </div>

                {/* the vacancy badge breaks out of the hero */}
                <div className="relative flex justify-center" style={{ zIndex: 3, marginTop: fs(3.4), marginBottom: `calc(${fs(17)} * -1)` }}>
                    <div
                        className="relative flex flex-col items-center justify-center rounded-full text-center"
                        style={{ width: fs(34), height: fs(34), background: '#fff', border: `${fs(1.1)} solid ${p.ink}`, boxShadow: `0 0 0 ${fs(1)} ${p.accent}, 0 1cqw 2.4cqw rgba(0,0,0,0.3)` }}
                    >
                        <div style={{ color: p.ink, fontSize: fs(3), fontWeight: 800, lineHeight: 1 }}>{f.label.vacancy}</div>
                        <div style={{ color: p.number, fontSize: `calc(${15 * numberScale(f.vacancy)}cqw * var(--fs, 1))`, fontWeight: 900, lineHeight: 1, marginTop: fs(0.6) }}>{f.vacancy}</div>
                        <div style={{ color: p.ink, fontSize: fs(3.6), fontWeight: 800, lineHeight: 1, marginTop: fs(0.4) }}>{f.label.unit}</div>
                    </div>
                </div>
            </div>

            {/* ── lower half ── */}
            <div className="flex flex-1 flex-col justify-center" style={{ background: p.paper, padding: `${fs(21)} 5.5cqw ${fs(3)}`, gap: fs(2.6), borderRadius: `${fs(7)} ${fs(7)} 0 0`, marginTop: `calc(${fs(5)} * -1)`, position: 'relative' }}>
                <div className="flex justify-center" style={{ gap: fs(2) }}>
                    <div className="flex items-center whitespace-nowrap" style={{ background: '#fff', border: `0.3cqw solid ${p.ink}`, borderRadius: '99px', padding: `${fs(1)} ${fs(2.6)}`, gap: fs(1.2), color: p.ink, fontSize: fs(3), fontWeight: 700 }}>
                        <IconCalendarEvent style={{ width: fs(3.4), height: fs(3.4), color: p.hero }} stroke={2.2} />
                        <div>{f.label.start}: {f.start}</div>
                    </div>
                    <div className="flex items-center whitespace-nowrap" style={{ background: p.accent, border: `0.3cqw solid ${p.ink}`, borderRadius: '99px', padding: `${fs(1)} ${fs(2.6)}`, gap: fs(1.2), color: p.ink, fontSize: fs(3), fontWeight: 900 }}>
                        <IconClockHour4 style={{ width: fs(3.4), height: fs(3.4), color: p.ink }} stroke={2.4} />
                        <div>{f.label.end}: {f.end}</div>
                    </div>
                </div>

                <div className="grid grid-cols-2" style={{ gap: fs(2.4) }}>
                    <InfoCard p={p} Icon={IconBooks} label={f.label.edu}>
                        <div style={{ color: p.ink, fontSize: `calc(${3.3 * eduScale}cqw * var(--fs, 1))`, fontWeight: 800, lineHeight: 1.22 }}>{f.edu}</div>
                    </InfoCard>
                    <InfoCard p={p} Icon={IconUser} label={f.label.age}>
                        <div style={{ color: p.ink, fontSize: `calc(${3.3 * ageScale}cqw * var(--fs, 1))`, fontWeight: 800, lineHeight: 1.22 }}>{f.age}</div>
                    </InfoCard>
                </div>

                <div className="text-center" style={{ color: p.muted, fontSize: fs(2.7), fontWeight: 600 }}>✓ {f.label.promo}</div>

                <div className="flex items-center justify-between" style={{ background: p.cta, borderRadius: '99px', padding: `${fs(1.3)} ${fs(1.4)} ${fs(1.3)} ${fs(5)}`, boxShadow: '0 0.8cqw 1.8cqw rgba(0,0,0,0.25)' }}>
                    <div style={{ color: p.ctaText, fontSize: fs(4.6), fontWeight: 900 }}>{f.label.cta}</div>
                    <div className="flex flex-none items-center justify-center rounded-full" style={{ width: fs(8.6), height: fs(8.6), background: p.accent }}>
                        <IconArrowRight style={{ width: fs(5), height: fs(5), color: p.ink }} stroke={3} />
                    </div>
                </div>
            </div>
        </div>
    );

    const footer = showFooter ? (
        <div className="text-center" style={{ background: p.footer, padding: `${fs(2.8)} 5cqw ${fs(3.2)}` }}>
            <div className="flex items-center justify-center" style={{ gap: fs(2.4) }}>
                {f.shop.logo && <img src={f.shop.logo} alt="" className="flex-none rounded-full object-contain" style={{ width: fs(8.6), height: fs(8.6), background: '#fff', padding: '0.7cqw' }} />}
                <div style={{ color: p.footerName, fontSize: fs(6.2), fontWeight: 900, lineHeight: 1.12, opacity: f.shop.placeholder ? 0.65 : 1 }}>{f.shop.nameShown}</div>
            </div>
            <div className="flex items-center justify-center" style={{ gap: fs(1.2), marginTop: fs(0.8), color: p.footerText, fontSize: fs(3.7), lineHeight: 1.25, opacity: f.shop.addressPlaceholder ? 0.65 : 0.95 }}>
                <IconMapPin style={{ width: fs(3.8), height: fs(3.8), color: p.footerName }} className="flex-none" />
                <div>{f.shop.addressShown}</div>
            </div>
            {f.shop.phone && (
                <div className="flex items-center justify-center" style={{ gap: fs(1.2), marginTop: fs(0.6), color: p.footerText, fontSize: fs(4.6), fontWeight: 800 }}>
                    <IconPhone style={{ width: fs(4.2), height: fs(4.2), color: p.footerName }} className="flex-none" />
                    <div>{f.shop.phone}</div>
                </div>
            )}
        </div>
    ) : null;

    return (
        <PosterSheet
            deps={[job, business, theme, showFooter, t]}
            className={className}
            fontFamily={FONT}
            background={p.paper}
            body={body}
            footer={footer}
        />
    );
}
